/**
 * useItemListData — fetches items and applies client-side search, filter, sort, group.
 */

import type { ObjectKind } from '@/components/ds/objectKinds';
import { useMemo } from 'react';
import type { Item } from '../../../../types/prioritization/lifecycle';
import type { FilterRule, FilterGroup, SortSpec } from './useItemListState';
import { computeBaseCompleteness } from '../../../../lib/itemReadiness';
import { useItemRelationshipsStore } from '../../../../stores/prioritization/itemRelationshipsStore';
import { useItemPoolsStore } from '../../../../stores/prioritization/itemPoolsStore';
import { useSessionStore } from '../../../../stores/prioritization/sessionStore';
import { useProposalPacketStore } from '../../../../stores/prioritization/proposalPacketStore';
import { useCollectionsStore } from '../../../../stores/prioritization/collectionsStore';
import { useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';
import { getDirectedRelationshipRolePresence } from '../../../../lib/directedRelationshipRoles';
import { ITEM_FIELD_BY_KEY } from '../config/itemFields';
import { usePipelineInputs } from '../../../../hooks/prioritization/usePipelineInputs';
import { classifyPipelineBucket, type PipelineBucket } from '../../../../lib/pipelineHealth';
import { evaluateNode, evaluateRule } from '../../../../lib/pql/evaluate';
import type { QueryNode } from '../../../../lib/pql/types';
import { buildItemMembership } from '../../../../lib/itemMembership';
import type {
  RelationshipStatus,
  ItemRelationshipGroupMembership,
} from '../../../../types/prioritization/itemRelationships';

// ============================================================================
// Types
// ============================================================================

/**
 * A confirmed cluster is the resolution of a duplicate — so the item list shows
 * it as ONE entry under the cluster's name rather than each member separately.
 * This is the metadata carried on that single entry row.
 */
export interface ClusterEntry {
  /** The item_relationship_group id of the cluster. */
  groupId: string;
  /** The cluster's display name (group title, falling back to its representative's title). */
  title: string;
  /** Member item ids that survived the current search/filter, in display order. */
  memberIds: string[];
  /**
   * The member whose field values back this row (the canonical member when the
   * cluster declares one). Row actions and navigation resolve to this item.
   */
  representativeId: string;
}

export interface ItemRow extends Item {
  /** Number of base-completeness fields missing (title, category, altitude, dedupe). Not a global readiness score. */
  missingBaseFields: number;
  /** True when all four base fields are present. Does not imply cycle-readiness. */
  hasBaseCompleteness: boolean;
  hasChildren: boolean;
  isStale: boolean;
  /** True when the item has non-empty description text. */
  hasDescription: boolean;
  /** True when the item has a parent item (parent_item_id is set). */
  hasParentItem: boolean;
  // Relationship presence (computed from relationships store)
  hasCluster: boolean;
  hasPackage: boolean;
  /** True when this item is in a Competing (Exclusive) relationship group. */
  hasCompeting: boolean;
  /**
   * @deprecated Prefer hasPredecessors / hasDependents. Kept so saved reports
   * that still filter on hasDependency continue to match either sequence role.
   */
  hasDependency: boolean;
  /** Sequence: must be addressed after one or more predecessors. */
  hasPredecessors: boolean;
  /** Sequence: one or more items are waiting on this. */
  hasDependents: boolean;
  /**
   * @deprecated Prefer hasCompositionParts / isPartOfComposition.
   */
  hasAggregation: boolean;
  /** Composition: this item is a whole with parts. */
  hasCompositionParts: boolean;
  /** Composition: this item is a part of a whole. */
  isPartOfComposition: boolean;
  /**
   * @deprecated Prefer hasElaborationPossibilities / isElaborationPossibility.
   */
  hasElaboration: boolean;
  /** Elaboration: parent need with one or more possibilities. */
  hasElaborationPossibilities: boolean;
  /** Elaboration: this item is a possibility on a continuum. */
  isElaborationPossibility: boolean;
  /** Contains: reporting parent with children. */
  hasContainsChildren: boolean;
  /** Contains: reporting child of a parent. */
  hasContainsParent: boolean;
  /** True when this item has at least one Related edge. */
  hasRelated: boolean;
  hasFrames: boolean;
  hasCollection: boolean;
  /** True when this item appears in any lineage event (split, merge, fork, supersede, retire). */
  hasLineage: boolean;
  /** IDs of all collections this item belongs to (may be empty). */
  collectionIds: string[];
  /** Names of all collections this item belongs to, for display (may be empty). */
  collectionNames: string[];
  /** IDs of package groups this item belongs to. */
  packageGroupIds: string[];
  /** IDs of cluster groups this item belongs to. */
  clusterGroupIds: string[];
  /** IDs of exclusive (competing) groups this item belongs to. */
  exclusiveGroupIds: string[];
  // Activity membership (derived from item pools and sessions)
  cycleIds: string[];
  trackIds: string[];
  sessionIds: string[];
  // Lineage actions (governance history)
  /** True when this item has been leadership-approved onto the Priority List. */
  isLeadershipApproved: boolean;
  /** True when at least one Catchball proposal exists for this item. */
  hasCatchballProposal: boolean;
  /**
   * Most-recent (or worst-case) Catchball proposal status for this item.
   * 'none' when no proposals exist.
   */
  catchballStatus: 'none' | 'pending' | 'accepted' | 'countered' | 'rejected' | 'withdrawn';
  /** True when this item is a member of at least one item pool. */
  inItemPool: boolean;
  /**
   * Where the item stands on the pipeline (lib/pipelineHealth.ts): the furthest
   * rung reached, or the exit it took. The same classifier the Pipeline Health
   * report counts with, so a funnel stage opens exactly the rows it counted.
   */
  pipelineStage: PipelineBucket;
  /**
   * Present when this row stands for a whole confirmed cluster rather than one
   * item. `id` is then a synthetic `cluster:<groupId>` key, not an item id —
   * resolve through `clusterEntry.representativeId` / `memberIds` before
   * touching the items store.
   */
  clusterEntry?: ClusterEntry;
  /** Present when this row is a member shown beneath its expanded cluster entry. */
  clusterMemberOf?: string;
}

export interface GroupedRows {
  key: string;
  label: string;
  /** The kind the grouping field names, so its header is marked (category, altitude, collection…). */
  kind?: ObjectKind;
  rows: ItemRow[];
  count: number;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Resolve a field's value on an enriched item row. Built-in fields resolve
 * through the itemFields registry; `meta:*` custom fields and unknown keys fall
 * back to item.metadata / the raw property. Exported so exports (e.g. the custom
 * report CSV) can render exactly the columns the table shows.
 */
export function getFieldValue(item: ItemRow, field: string): unknown {
  // Built-in fields resolve through the itemFields registry (config/itemFields.ts),
  // the single source of truth for a field's value. Fields without a `getValue`
  // (e.g. owner, metaCategory) fall through to the same tail as before.
  const def = ITEM_FIELD_BY_KEY[field];
  if (def?.getValue) return def.getValue(item);

  // Custom workspace attribute filters use the `meta:<attributeKey>` field id
  // convention (see config/filterFields.ts buildCustomFieldsGroup). Values
  // live in item.metadata, keyed by the attribute's key — not as a
  // top-level item property (see CustomFieldsSection.tsx for the same read pattern).
  if (field.startsWith('meta:')) {
    return item.metadata?.[field.slice(5)];
  }
  return (item as Record<string, unknown>)[field];
}

// Rule-matching semantics live in src/lib/pql/evaluate.ts — the single leaf
// evaluator shared by the filter builder, saved reports, and the PQL
// statement bar, so the two modes can never drift apart.
function matchesRule(item: ItemRow, rule: FilterRule): boolean {
  return evaluateRule(rule, (field) => getFieldValue(item, field));
}

function matchesGroup(item: ItemRow, group: FilterGroup): boolean {
  if (group.rules.length === 0) return true;
  return group.conjunction === 'OR'
    ? group.rules.some((r) => matchesRule(item, r))
    : group.rules.every((r) => matchesRule(item, r));
}

function compareValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  const mul = direction === 'asc' ? 1 : -1;
  if (a == null && b == null) return 0;
  if (a == null) return 1 * mul;
  if (b == null) return -1 * mul;
  if (a instanceof Date && b instanceof Date) return (a.getTime() - b.getTime()) * mul;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mul;
  return String(a).localeCompare(String(b)) * mul;
}

/**
 * Statuses at which a cluster is a settled fact rather than a suggestion.
 * Matches the repo-wide convention for a live relationship group (see
 * RelationshipHealthPage, TrackRelationshipsTab, RelationshipScenarioPage):
 * `active` is what the create-cluster path writes, `confirmed` what review
 * writes. A `candidate` cluster is an *unresolved* duplicate and must keep
 * showing its members individually.
 */
const SETTLED_CLUSTER_STATUSES: ReadonlySet<RelationshipStatus> = new Set<RelationshipStatus>([
  'active',
  'confirmed',
]);

/** Row id for a cluster entry. Never an item id — see ItemRow.clusterEntry. */
export function clusterEntryRowId(groupId: string): string {
  return `cluster:${groupId}`;
}

/** A settled cluster reduced to what the list needs to render it. */
export interface SettledCluster {
  groupId: string;
  /** Group title, empty when the cluster was never named. */
  title: string;
  memberIds: string[];
  /** The declared canonical member, when the cluster has one. */
  canonicalId?: string;
}

export interface SettledClusterIndex {
  byGroup: Map<string, SettledCluster>;
  /** itemId → groupId. First settled cluster wins if an item is in several. */
  groupByItem: Map<string, string>;
}

/**
 * Collapse each settled cluster in `sorted` into a single entry row.
 *
 * A confirmed cluster is the *resolution* of a duplicate, so its members go by
 * the cluster's name here rather than appearing one by one. The entry row is
 * synthetic: its id is `cluster:<groupId>`, its field values come from the
 * cluster's representative (the canonical member when one is declared), and its
 * title is the cluster's. An expanded cluster lists its members beneath it.
 *
 * Rows are emitted in `sorted` order; a cluster takes the position of its
 * first-sorted member. A cluster whose members were filtered down to one is not
 * collapsed — that lone item shows under its own name rather than hiding behind
 * a cluster of one.
 */
export function collapseSettledClusters(
  sorted: ItemRow[],
  clusters: SettledClusterIndex,
  expandedClusters?: Set<string>,
): ItemRow[] {
  if (clusters.byGroup.size === 0) return sorted;

  // Members that survived search/filter, in sorted order, per cluster.
  const presentByGroup = new Map<string, ItemRow[]>();
  for (const row of sorted) {
    const groupId = clusters.groupByItem.get(row.id);
    if (!groupId) continue;
    const arr = presentByGroup.get(groupId);
    if (arr) arr.push(row);
    else presentByGroup.set(groupId, [row]);
  }

  const out: ItemRow[] = [];
  const emitted = new Set<string>();
  for (const row of sorted) {
    const groupId = clusters.groupByItem.get(row.id);
    if (!groupId) {
      out.push(row);
      continue;
    }
    if (emitted.has(groupId)) continue;
    emitted.add(groupId);

    const present = presentByGroup.get(groupId) ?? [];
    if (present.length < 2) {
      out.push(row);
      continue;
    }

    const cluster = clusters.byGroup.get(groupId)!;
    const representative = present.find((r) => r.id === cluster.canonicalId) ?? present[0];
    const title = cluster.title || representative.title;
    out.push({
      ...representative,
      id: clusterEntryRowId(groupId),
      title,
      clusterEntry: {
        groupId,
        title,
        memberIds: present.map((r) => r.id),
        representativeId: representative.id,
      },
    });
    if (expandedClusters?.has(groupId)) {
      for (const member of present) out.push({ ...member, clusterMemberOf: groupId });
    }
  }
  return out;
}

// ============================================================================
// Hook
// ============================================================================

export function useItemListData(
  items: Item[],
  searchQuery: string,
  filterGroups: FilterGroup[],
  sort: SortSpec[],
  groupBy: string | null,
  /**
   * When provided (statement mode), this PQL AST supersedes `filterGroups`
   * for matching — it can express negation and nesting the builder can't.
   * `null` means "statement mode, empty query" (match everything);
   * `undefined` means builder mode (use filterGroups).
   */
  queryAst?: QueryNode | null,
  /**
   * Cluster group ids the user has expanded. A collapsed confirmed cluster
   * renders as one entry; an expanded one lists its members beneath it.
   */
  expandedClusters?: Set<string>
) {
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const queueNameMap = useMemo(
    () =>
      Object.fromEntries(
        Array.from(orgUnits.values()).map((ou) => [
          ou.id,
          ou.displayName?.trim() || ou.name?.trim() || ou.id,
        ]),
      ),
    [orgUnits],
  );

  const groups = useItemRelationshipsStore((s) => s.groups);
  const groupMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const edges = useItemRelationshipsStore((s) => s.edges);
  const lineageEvents = useItemRelationshipsStore((s) => s.lineageEvents);
  const collectionMemberships = useCollectionsStore((s) => s.memberships);
  const collections = useCollectionsStore((s) => s.collections);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const sessions = useSessionStore((s) => s.sessions);
  const pipelineInputs = usePipelineInputs();
  const proposals = useProposalPacketStore((s) => s.proposals);

  // Build item → cycle / track / session membership maps from pools and sessions
  // One copy of item → cycle / track / session membership (lib/itemMembership).
  const membershipMaps = useMemo(() => buildItemMembership(itemPools, sessions), [itemPools, sessions]);

  // Build item pool membership set
  const itemPoolMembers = useMemo(() => {
    const inPool = new Set<string>();
    for (const pool of itemPools ?? []) {
      for (const id of pool.priorityIds ?? []) inPool.add(id);
    }
    return inPool;
  }, [itemPools]);

  // Build catchball proposal presence maps
  const catchballPresence = useMemo(() => {
    // Priority: pending > countered > rejected > withdrawn > accepted
    // (so an item with an open/active proposal shows as pending/countered first)
    const STATUS_PRIORITY: Record<string, number> = {
      pending: 5, countered: 4, rejected: 3, withdrawn: 2, accepted: 1,
    };
    const statusByItem = new Map<string, string>();
    for (const p of proposals ?? []) {
      if (!p.itemId) continue;
      const current = statusByItem.get(p.itemId);
      const currentPrio = current ? (STATUS_PRIORITY[current] ?? 0) : 0;
      const newPrio = STATUS_PRIORITY[p.status ?? ''] ?? 0;
      if (!current || newPrio > currentPrio) {
        statusByItem.set(p.itemId, p.status ?? 'pending');
      }
    }
    return statusByItem;
  }, [proposals]);

  // Build collection name lookup: id → name
  const collectionNameMap = useMemo(
    () => Object.fromEntries(collections.map((c) => [c.id, c.name])),
    [collections],
  );

  // Build item → collectionIds map (many-to-many)
  const itemToCollectionIds = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of collectionMemberships) {
      const existing = map.get(m.itemId);
      if (existing) existing.push(m.collectionId);
      else map.set(m.itemId, [m.collectionId]);
    }
    return map;
  }, [collectionMemberships]);

  // Build per-item relationship presence lookup sets (and specific group ID lists)
  const relationshipPresence = useMemo(() => {
    const clusters = new Set<string>();
    const packages = new Set<string>();
    const exclusives = new Set<string>();
    // item → group IDs for specific-group filtering
    const itemToPackageGroups = new Map<string, string[]>();
    const itemToClusterGroups = new Map<string, string[]>();
    const itemToExclusiveGroups = new Map<string, string[]>();
    for (const m of groupMemberships) {
      const g = groups.find((gr) => gr.id === m.groupId);
      if (g?.type === 'cluster') {
        clusters.add(m.itemId);
        const arr = itemToClusterGroups.get(m.itemId) ?? [];
        arr.push(m.groupId);
        itemToClusterGroups.set(m.itemId, arr);
      } else if (g?.type === 'package') {
        packages.add(m.itemId);
        const arr = itemToPackageGroups.get(m.itemId) ?? [];
        arr.push(m.groupId);
        itemToPackageGroups.set(m.itemId, arr);
      } else if (g?.type === 'exclusive') {
        exclusives.add(m.itemId);
        const arr = itemToExclusiveGroups.get(m.itemId) ?? [];
        arr.push(m.groupId);
        itemToExclusiveGroups.set(m.itemId, arr);
      }
    }
    const directedRoles = getDirectedRelationshipRolePresence(edges);
    const collections = new Set<string>();
    for (const m of collectionMemberships) {
      collections.add(m.itemId);
    }
    // Items that appear in any lineage event (source or target)
    const lineage = new Set<string>();
    for (const le of lineageEvents ?? []) {
      for (const id of [...(le.sourceItemIds ?? []), ...(le.targetItemIds ?? [])]) {
        lineage.add(id);
      }
    }
    return {
      clusters,
      packages,
      exclusives,
      directedRoles,
      collections,
      lineage,
      itemToPackageGroups,
      itemToClusterGroups,
      itemToExclusiveGroups,
    };
  }, [groups, groupMemberships, edges, collectionMemberships, lineageEvents]);

  // Settled clusters — the ones the item list collapses into a single entry.
  // A cluster of fewer than two members is not a cluster; it renders normally.
  const settledClusters = useMemo(() => {
    const membersByGroup = new Map<string, ItemRelationshipGroupMembership[]>();
    for (const m of groupMemberships) {
      const arr = membersByGroup.get(m.groupId);
      if (arr) arr.push(m);
      else membersByGroup.set(m.groupId, [m]);
    }
    const byGroup = new Map<string, SettledCluster>();
    const groupByItem = new Map<string, string>();
    for (const g of groups) {
      if (g.type !== 'cluster' || !SETTLED_CLUSTER_STATUSES.has(g.status)) continue;
      const ms = membersByGroup.get(g.id) ?? [];
      if (ms.length < 2) continue;
      byGroup.set(g.id, {
        groupId: g.id,
        title: g.title?.trim() ?? '',
        memberIds: ms.map((m) => m.itemId),
        canonicalId: ms.find((m) => m.membershipRole === 'canonical')?.itemId,
      });
      for (const m of ms) {
        if (!groupByItem.has(m.itemId)) groupByItem.set(m.itemId, g.id);
      }
    }
    return { byGroup, groupByItem } satisfies SettledClusterIndex;
  }, [groups, groupMemberships]);

  // Enrich items with computed fields
  const enrichedItems: ItemRow[] = useMemo(() => {
    const now = Date.now();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return items.map((item) => {
      const baseCompleteness = computeBaseCompleteness(item);
      const updatedMs = item.updatedAt instanceof Date ? item.updatedAt.getTime() : new Date(item.updatedAt).getTime();
      return {
        ...item,
        missingBaseFields: [baseCompleteness.hasTitle, baseCompleteness.hasCategory, baseCompleteness.hasAltitude].filter((v) => !v).length,
        hasBaseCompleteness: baseCompleteness.isBaseComplete,
        hasChildren: (item.childItemIds?.length ?? 0) > 0,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        isStale: now - updatedMs > thirtyDays && (item as any).exit_status !== 'archived',
        hasDescription: !!item.description?.trim(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hasParentItem: !!(item as any).parentItemId || !!(item as any).parent_item_id,
        hasCluster: relationshipPresence.clusters.has(item.id),
        hasPackage: relationshipPresence.packages.has(item.id),
        hasCompeting: relationshipPresence.exclusives.has(item.id),
        hasPredecessors: relationshipPresence.directedRoles.hasPredecessors.has(item.id),
        hasDependents: relationshipPresence.directedRoles.hasDependents.has(item.id),
        hasDependency:
          relationshipPresence.directedRoles.hasPredecessors.has(item.id) ||
          relationshipPresence.directedRoles.hasDependents.has(item.id),
        hasCompositionParts: relationshipPresence.directedRoles.hasCompositionParts.has(item.id),
        isPartOfComposition: relationshipPresence.directedRoles.isPartOfComposition.has(item.id),
        hasAggregation:
          relationshipPresence.directedRoles.hasCompositionParts.has(item.id) ||
          relationshipPresence.directedRoles.isPartOfComposition.has(item.id),
        hasElaborationPossibilities:
          relationshipPresence.directedRoles.hasElaborationPossibilities.has(item.id),
        isElaborationPossibility:
          relationshipPresence.directedRoles.isElaborationPossibility.has(item.id),
        hasElaboration:
          relationshipPresence.directedRoles.hasElaborationPossibilities.has(item.id) ||
          relationshipPresence.directedRoles.isElaborationPossibility.has(item.id),
        hasContainsChildren: relationshipPresence.directedRoles.hasContainsChildren.has(item.id),
        hasContainsParent: relationshipPresence.directedRoles.hasContainsParent.has(item.id),
        hasRelated: relationshipPresence.directedRoles.hasRelated.has(item.id),
        hasFrames: Array.isArray(item.frames) && item.frames.length > 1,
        hasCollection: relationshipPresence.collections.has(item.id),
        hasLineage: relationshipPresence.lineage.has(item.id),
        collectionIds: itemToCollectionIds.get(item.id) ?? [],
        collectionNames: (itemToCollectionIds.get(item.id) ?? []).map((id) => collectionNameMap[id] ?? id),
        packageGroupIds: relationshipPresence.itemToPackageGroups.get(item.id) ?? [],
        clusterGroupIds: relationshipPresence.itemToClusterGroups.get(item.id) ?? [],
        exclusiveGroupIds: relationshipPresence.itemToExclusiveGroups.get(item.id) ?? [],
        cycleIds: Array.from(membershipMaps.itemToCycles.get(item.id) ?? []),
        trackIds: Array.from(membershipMaps.itemToTracks.get(item.id) ?? []),
        sessionIds: Array.from(membershipMaps.itemToSessions.get(item.id) ?? []),
        // Lineage actions
        isLeadershipApproved: Boolean(item.leadershipApproved),
        hasCatchballProposal: catchballPresence.has(item.id),
        catchballStatus: (catchballPresence.get(item.id) ?? 'none') as ItemRow['catchballStatus'],
        inItemPool: itemPoolMembers.has(item.id),
        pipelineStage: classifyPipelineBucket(item, pipelineInputs),
      };
    });
  }, [items, relationshipPresence, membershipMaps, catchballPresence, itemPoolMembers, itemToCollectionIds, collectionNameMap, pipelineInputs]);

  // Search
  const searched = useMemo(() => {
    if (!searchQuery.trim()) return enrichedItems;
    const q = searchQuery.toLowerCase();
    return enrichedItems.filter(
      (item) =>
        item.title?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    );
  }, [enrichedItems, searchQuery]);

  // Filter — statement mode evaluates the PQL AST; builder mode ANDs the
  // filter groups (rules within a group use the group's conjunction).
  const filtered = useMemo(() => {
    if (queryAst !== undefined) {
      if (queryAst === null) return searched;
      return searched.filter((item) =>
        evaluateNode(queryAst, (field) => getFieldValue(item, field)),
      );
    }
    if (filterGroups.length === 0) return searched;
    return searched.filter((item) => filterGroups.every((g) => matchesGroup(item, g)));
  }, [searched, filterGroups, queryAst]);

  // Sort
  const sorted = useMemo(() => {
    if (sort.length === 0) return filtered;
    return [...filtered].sort((a, b) => {
      for (const spec of sort) {
        const va = getFieldValue(a, spec.field);
        const vb = getFieldValue(b, spec.field);
        const cmp = compareValues(va, vb, spec.direction);
        if (cmp !== 0) return cmp;
      }
      return 0;
    });
  }, [filtered, sort]);

  // Collapse settled clusters — the item list's display truth (see
  // collapseSettledClusters). `rows` (returned below) stays the flat, real-item
  // list, so charts, exports, focus mode and bulk actions keep operating on
  // items and never on a synthetic entry.
  const displayRows: ItemRow[] = useMemo(
    () => collapseSettledClusters(sorted, settledClusters, expandedClusters),
    [sorted, settledClusters, expandedClusters],
  );

  // Group
  const grouped: GroupedRows[] | null = useMemo(() => {
    if (!groupBy) return null;

    // Collection grouping: many-to-many — an item may appear in multiple groups
    if (groupBy === 'collection') {
      const NO_COLL = '__no_collection__';
      const buckets = new Map<string, ItemRow[]>();
      for (const item of displayRows) {
        if (item.collectionIds.length === 0) {
          const b = buckets.get(NO_COLL) ?? [];
          b.push(item);
          buckets.set(NO_COLL, b);
        } else {
          for (const cId of item.collectionIds) {
            const b = buckets.get(cId) ?? [];
            b.push(item);
            buckets.set(cId, b);
          }
        }
      }
      return Array.from(buckets.entries())
        .map(([key, rows]) => ({
          key,
          label: key === NO_COLL ? 'No collection' : (collectionNameMap[key] ?? key),
          kind: 'collection' as const,
          rows,
          count: rows.length,
        }))
        .sort((a, b) => {
          if (a.key === NO_COLL) return 1;
          if (b.key === NO_COLL) return -1;
          return b.count - a.count;
        });
    }

    const groups = new Map<string, ItemRow[]>();
    for (const item of displayRows) {
      const val = String(getFieldValue(item, groupBy) ?? 'Unset');
      const existing = groups.get(val);
      if (existing) existing.push(item);
      else groups.set(val, [item]);
    }
    return Array.from(groups.entries())
      .map(([key, rows]) => ({
        key,
        label: formatGroupLabel(groupBy, key, queueNameMap),
        kind: GROUP_FIELD_KIND[groupBy],
        rows,
        count: rows.length,
      }))
      .sort((a, b) => b.count - a.count);
  }, [displayRows, groupBy, queueNameMap, collectionNameMap]);

  return {
    rows: sorted,
    /**
     * What the table/cards render: same rows, but each settled cluster collapsed
     * into one entry. Selection and row actions must resolve `clusterEntry`
     * before touching the items store.
     */
    displayRows,
    grouped,
    totalCount: items.length,
    filteredCount: sorted.length,
    searchCount: searched.length,
  };
}

/** Grouping fields that name a kind of thing; their group headers carry its marker. */
const GROUP_FIELD_KIND: Record<string, ObjectKind | undefined> = {
  itemCategory: 'category',
  altitude: 'altitude',
  strategicBucket: 'strategic_bucket',
  source: 'source',
  collection: 'collection',
};

function formatGroupLabel(
  field: string,
  value: string,
  queueNameMap: Record<string, string> = {},
): string {
  if (value === 'Unset' || value === 'undefined' || value === 'null') return 'Unset';
  if (field === 'exit_status' || field === 'detailedState') {
    const labels: Record<string, string> = { active: 'Active', deferred: 'Deferred', archived: 'Archived' };
    return labels[String(value)] ?? String(value);
  }
  if (field === 'altitude') {
    const labels: Record<string, string> = { '1': 'Level 1', '2': 'Level 2', '3': 'Level 3', '4': 'Level 4', '5': 'Level 5' };
    return labels[value] ?? `Level ${value}`;
  }
  if (field === 'itemCategory') return value.charAt(0).toUpperCase() + value.slice(1);
  if (field === 'queue') {
    const name = queueNameMap[value];
    if (!name || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(name)) return 'General';
    return name;
  }
  if (field === 'strategicBucket') {
    const labels: Record<string, string> = {
      growth: 'Growth', commitments: 'Commitments', reliability: 'Reliability',
      platform: 'Platform', compliance: 'Compliance',
    };
    return labels[value] ?? value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}
